import { create } from 'zustand';
import { safeGetItem, safeSetItem } from '../lib/safeStorage';

/** Chromium's `beforeinstallprompt` event — not in lib.dom.d.ts, hence the manual shape. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export type InstallMode = 'native' | 'ios' | null;

const LOOK_SAVED_KEY = 'pp.install.lookSaved';
const SESSIONS_KEY = 'pp.install.sessions';
const ASKS_KEY = 'pp.install.asks';

// §11.11: "ask at most twice, ever."
const MAX_ASKS = 2;
// §11.11 / SCREENS.md "PWA — Install Prompt": trigger on the first saved Look OR the third
// session, whichever lands first.
const SESSION_TRIGGER_DAYS = 3;
// "Capped small" per task-16-brief.md — this only ever needs to distinguish "< 3" from ">= 3"
// distinct days, so there's no reason to let the array grow without bound over a long-lived
// install.
const MAX_TRACKED_SESSIONS = 8;

// safeGetItem/safeSetItem (private-browsing/partitioned-storage-safe localStorage access — this
// module runs both at import time via App.tsx, so an unguarded throw would crash app BOOT, not
// just this one feature) now live in web/src/lib/safeStorage.ts — Task 16's bagCache.ts needs the
// exact same guard for its own offline snapshot, so this was extracted rather than duplicated.

function readSessions(): string[] {
  try {
    const parsed = JSON.parse(safeGetItem(SESSIONS_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readAsks(): number {
  const n = Number(safeGetItem(ASKS_KEY));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function readLookSaved(): boolean {
  return safeGetItem(LOOK_SAVED_KEY) === 'true';
}

// MINOR fix (reviewer finding): `Date#toISOString()` is UTC, not the player's local calendar day
// — this project has already been bitten by exactly this class of bug once (see docs/RULINGS.md
// / the `deriveAgeBand` C3 fix in `api/auth.ts`, which parses birth dates by numeric components
// compared against LOCAL `Date` getters for the same reason). A player just west of UTC whose
// local day hasn't rolled over yet would have their session logged under tomorrow's UTC date,
// and vice versa east of UTC — silently corrupting the "3 distinct session days" trigger count.
// Mirrors `deriveAgeBand`'s own local-getter approach rather than any UTC-based API.
function localDateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type InstallState = {
  deferredPrompt: BeforeInstallPromptEvent | null;
  lookSaved: boolean;
  sessionDays: string[];
  asks: number;
  captureDeferredPrompt: (e: BeforeInstallPromptEvent) => void;
  clearDeferredPrompt: () => void;
  markLookSaved: () => void;
  markSession: () => void;
  recordAsk: () => void;
  /** Test-only: re-reads localStorage (after a test clears it) and drops any stashed event —
   *  mirrors `useOutfitDraft`'s own `.getState().reset(...)` convention for module-level stores
   *  that need a clean slate between tests. Real app code never calls this. */
  reset: () => void;
};

/**
 * Module-level store (same pattern as `wardrobe/useOutfitDraft.ts` / `shell/scrollStore.ts`) —
 * `beforeinstallprompt` can fire at any point after this module is first imported, well before
 * any component that cares (`InstallSheet`) has mounted, so the captured event has to live
 * somewhere that survives independent of any one component's lifecycle.
 */
export const useInstallStore = create<InstallState>((set, get) => ({
  deferredPrompt: null,
  lookSaved: readLookSaved(),
  sessionDays: readSessions(),
  asks: readAsks(),
  captureDeferredPrompt: (e) => set({ deferredPrompt: e }),
  clearDeferredPrompt: () => set({ deferredPrompt: null }),
  markLookSaved: () => {
    safeSetItem(LOOK_SAVED_KEY, 'true');
    set({ lookSaved: true });
  },
  markSession: () => {
    const today = localDateKey();
    const days = get().sessionDays;
    if (days.includes(today)) return;
    const next = [...days, today].slice(-MAX_TRACKED_SESSIONS);
    safeSetItem(SESSIONS_KEY, JSON.stringify(next));
    set({ sessionDays: next });
  },
  recordAsk: () => {
    const next = get().asks + 1;
    safeSetItem(ASKS_KEY, String(next));
    set({ asks: next });
  },
  reset: () => set({
    deferredPrompt: null,
    lookSaved: readLookSaved(),
    sessionDays: readSessions(),
    asks: readAsks(),
  }),
}));

// Registered once, at module import — App.tsx pulls this module in at boot (directly, calling
// `markSession()`, and transitively via `InstallSheet`), which is before any user interaction
// can occur, so this is reliably wired up before Chromium ever has a chance to fire the real
// event. §11.11: "Capture `beforeinstallprompt` and defer it. Never fire on first visit." —
// `preventDefault()` is exactly that deferral; nothing here shows any UI on its own.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    useInstallStore.getState().captureDeferredPrompt(e as BeforeInstallPromptEvent);
  });
}

/** Fires the player's first-Look-saved install-prompt trigger. Wired into `SaveLookSheet`'s
 *  success path (`WardrobeScreen.handleLookSaved`). Idempotent — safe to call on every save,
 *  not just the first. */
export function markLookSaved() {
  useInstallStore.getState().markLookSaved();
}

/** Records today as a distinct session day (deduplicated, so multiple calls in one day are a
 *  no-op after the first). Called once at app boot (`App.tsx`). */
export function markSession() {
  useInstallStore.getState().markSession();
}

// jsdom (this project's pinned environment) implements neither `matchMedia` nor
// `navigator.standalone` — both accessed defensively (optional chaining / try-catch) so this
// never throws in a test that hasn't mocked them, and simply reads as "not installed", the safe
// default.
// Exported (Task 23): `pushPrompt.ts`'s PushPrePromptSheet needs the exact same "iOS Safari, not
// installed to Home Screen" detection for its own §11.11 ios-not-installed variant — same
// "extract for reuse rather than fork a second copy" reasoning as safeStorage.ts's own header
// comment.
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  let standalone = false;
  try {
    standalone = Boolean(window.matchMedia?.('(display-mode: standalone)').matches);
  } catch {
    standalone = false;
  }
  const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return standalone || iosStandalone;
}

// iOS Safari is the only browser that both (a) never fires `beforeinstallprompt` and (b) is the
// one platform §11.11 specifically calls out for the illustrated Add-to-Home-Screen sheet.
// Other iOS browsers (Chrome/Firefox/Edge for iOS) are all still WebKit under the hood but carry
// their own UA token, which excludes them here — they get no install affordance at all, same as
// desktop, rather than a set of instructions written for Safari's actual share-sheet UI.
export function isIOSSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOSDevice && !isOtherIOSBrowser;
}

export type InstallPromptControls = {
  eligible: boolean;
  mode: InstallMode;
  /** Native: fires the real, stashed OS prompt (Chromium's `deferredPrompt.prompt()`) — per
   *  §11.11 this is only ever called from behind the in-app pre-prompt sheet's own "Install"
   *  button, never automatically. Records an ask ONLY if the player dismisses the OS dialog
   *  itself ("a dismissal is an answer") — accepting spends nothing, since installing makes
   *  further asks moot anyway. No-op in 'ios' mode: there's no OS API to call — the InstallSheet
   *  IS the whole prompt there, its own "Got it" button calling `dismiss()` instead. */
  promptNow: () => Promise<void>;
  /** Any other close of the in-app pre-prompt (native "Not now", iOS "Got it", Escape,
   *  backdrop) — always records an ask. */
  dismiss: () => void;
};

/**
 * §11.11 / SCREENS.md "PWA — Install Prompt". Eligible exactly when: triggered (first Look
 * saved OR 3rd distinct session day) AND fewer than 2 asks recorded so far AND not already
 * installed AND a prompt is actually possible (a stashed native event, or iOS Safari detected).
 */
export function useInstallPrompt(): InstallPromptControls {
  const deferredPrompt = useInstallStore((s) => s.deferredPrompt);
  const lookSaved = useInstallStore((s) => s.lookSaved);
  const sessionDays = useInstallStore((s) => s.sessionDays);
  const asks = useInstallStore((s) => s.asks);
  const recordAsk = useInstallStore((s) => s.recordAsk);
  const clearDeferredPrompt = useInstallStore((s) => s.clearDeferredPrompt);

  let mode: InstallMode = null;
  if (deferredPrompt) mode = 'native';
  else if (isIOSSafari()) mode = 'ios';

  const triggered = lookSaved || sessionDays.length >= SESSION_TRIGGER_DAYS;
  const eligible = triggered && asks < MAX_ASKS && !isInstalled() && mode !== null;

  async function promptNow() {
    if (mode !== 'native' || !deferredPrompt) return;
    // One-shot: Chromium's own event can only be `.prompt()`-ed once, and clearing this
    // immediately also prevents a rapid double-click from firing it twice.
    clearDeferredPrompt();
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'dismissed') recordAsk();
  }

  function dismiss() {
    recordAsk();
  }

  return { eligible, mode, promptNow, dismiss };
}
